/**
 * News Tracking Utilities
 *
 * LocalStorage-based tracking for news visits and read status
 * Used to show "Co nowego" badge with unread count
 *
 * @module newsTracking
 */

import type { NewsItem } from '../data/news/newsTypes';

// LocalStorage keys
const LAST_VISIT_KEY = 'news_last_visit';
const READ_NEWS_KEY = 'news_read_items';

/**
 * Get the timestamp of the last visit to the news page
 * @returns Date object or null if never visited
 */
export function getLastVisit(): Date | null {
  const timestamp = localStorage.getItem(LAST_VISIT_KEY);
  if (!timestamp) {
    return null;
  }

  const date = new Date(timestamp);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Set the last visit timestamp to now
 */
export function setLastVisit(): void {
  localStorage.setItem(LAST_VISIT_KEY, new Date().toISOString());
}

/**
 * Get array of read news IDs
 * @returns Array of news IDs that have been read
 */
export function getReadNewsIds(): string[] {
  const stored = localStorage.getItem(READ_NEWS_KEY);
  if (!stored) {
    return [];
  }

  try {
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Mark a news item as read
 * @param newsId - ID of the news item to mark as read
 */
export function markNewsAsRead(newsId: string): void {
  const readIds = getReadNewsIds();

  // Don't add duplicates
  if (!readIds.includes(newsId)) {
    readIds.push(newsId);
    localStorage.setItem(READ_NEWS_KEY, JSON.stringify(readIds));
  }
}

/**
 * Calculate the number of unread news items
 * @param allNews - All available news items
 * @returns Number of unread news items
 */
export function getUnreadNewsCount(allNews: NewsItem[]): number {
  const lastVisit = getLastVisit();
  const readIds = getReadNewsIds();

  // If never visited, all news are unread
  if (!lastVisit) {
    return allNews.length;
  }

  // Count news items that:
  // 1. Were published after last visit OR
  // 2. Were not explicitly marked as read
  return allNews.filter((item) => {
    const newsDate = new Date(item.date);
    const isNew = newsDate > lastVisit;
    const wasRead = readIds.includes(item.id);

    return isNew || !wasRead;
  }).length;
}

/**
 * Check if a specific news item is unread
 * @param newsItem - News item to check
 * @returns True if the news item is unread
 */
export function isNewsUnread(newsItem: NewsItem): boolean {
  const lastVisit = getLastVisit();
  const readIds = getReadNewsIds();

  // If never visited, item is unread
  if (!lastVisit) {
    return true;
  }

  // Check if item was published after last visit
  const newsDate = new Date(newsItem.date);
  if (newsDate > lastVisit) {
    return true;
  }

  // Check if item was explicitly marked as read
  return !readIds.includes(newsItem.id);
}

/**
 * Clear all tracking data (for testing or privacy)
 */
export function clearTrackingData(): void {
  localStorage.removeItem(LAST_VISIT_KEY);
  localStorage.removeItem(READ_NEWS_KEY);
}
